import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Preview as PreviewIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { ApiManager } from "../ApiClient";
import axios from "axios";
import html2canvas from "html2canvas";
import IconHelper from "../Helpers/IconHelper";
import ColorHelper from "../Helpers/ColorHelper";

// import the font
import COPRGTL from "../Assets/Fonts/COPRGTL.TTF";
import Gabriola from "../Assets/Fonts/Gabriola.ttf";




// ============================================================================
// DYNAMIC FONT COMPONENT
// ============================================================================
const DynamicFontText = ({ fontName, fontPath, style = {}, children }) => {
  const [loaded, setLoaded] = useState(false);

  // Font files mapping
  const localFonts = {
    "/media/COPRGTL.TTF": COPRGTL,
    "/media/Gabriola.ttf": Gabriola,
  };

  // Local Font Loading with mapping
  useEffect(() => {
    if (!fontName || !fontPath) return;

    // 🔹 Local mapping
    const localFonts = {
      "/media/COPRGTL.TTF": COPRGTL,
      "/media/Gabriola.ttf": Gabriola,
    };

    // 🔹 Resolve correct font source
    const resolvedPath = localFonts[fontPath] || fontPath;

    // Prevent reloading if already loaded
    if (document.fonts.check(`1em ${fontName}`)) {
      setLoaded(true);
      return;
    }

    const font = new FontFace(fontName, `url(${resolvedPath})`);
    font.load()
      .then((loadedFace) => {
        document.fonts.add(loadedFace);
        setLoaded(true);
      })
      .catch((err) => {
        console.error(`Failed to load font ${fontName}`, err);
      });
  }, [fontName, fontPath]);

  return (
    <div style={{ ...style, fontFamily: loaded ? fontName : 'sans-serif' }}>
      {children}
    </div>
  );
};

// ============================================================================
// FONT LOADING UTILITY
// ============================================================================
const FontLoader = {
  loadedFonts: new Set(),

  parseFontPath: (fontPath) => {
    if (!fontPath) return { family: 'Arial', weight: 400 };

    const parts = fontPath.split('/');
    const fileName = parts[parts.length - 1].replace('.ttf', '');
    const family = parts[0];

    const weightMap = {
      'Thin': 100,
      'ExtraLight': 200,
      'Light': 300,
      'Regular': 400,
      'Medium': 500,
      'SemiBold': 600,
      'Bold': 700,
      'ExtraBold': 800,
      'Black': 900,
    };

    let weight = 400;
    for (const [key, value] of Object.entries(weightMap)) {
      if (fileName.includes(key)) {
        weight = value;
        break;
      }
    }

    return { family, weight, fileName };
  },

  loadGoogleFont: async (family, weight) => {
    const fontKey = `${family}-${weight}`;

    if (FontLoader.loadedFonts.has(fontKey)) {
      return true;
    }

    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@${weight}&display=swap`;
      link.rel = 'stylesheet';

      link.onload = () => {
        FontLoader.loadedFonts.add(fontKey);
        resolve(true);
      };

      link.onerror = () => {
        console.warn(`Failed to load font: ${family}`);
        resolve(false);
      };

      document.head.appendChild(link);

      setTimeout(() => resolve(false), 3000);
    });
  },

  loadFont: async (fontPath) => {
    const { family, weight } = FontLoader.parseFontPath(fontPath);
    await FontLoader.loadGoogleFont(family, weight);
    return { family, weight };
  },

  getFontFamily: (fontPath) => {
    const { family } = FontLoader.parseFontPath(fontPath);
    return `'${family}', Arial, sans-serif`;
  },

  getFontWeight: (fontPath) => {
    const { weight } = FontLoader.parseFontPath(fontPath);
    return weight;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const EventCertificateGenerator = ({
  processedImages,
  eventFields,
  onNavigateToImageProcessor,
  onReprocessImage,
  onClose,
}) => {
  const snack = useSnackbar();
  const [runnerName, setRunnerName] = useState("");
  const [loading, setLoading] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [scaleFactor, setScaleFactor] = useState(1);

  // Refs
  const previewContainerRef = useRef(null);
  const downloadContainerRef = useRef(null);

  // Template data
  const [templateImage, setTemplateImage] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [templateDimensions, setTemplateDimensions] = useState({
    width: 3840,
    height: 2560
  });
  const [achievementId, setAchievementId] = useState(null);
  const [seriesId, setSeriesId] = useState(null);
  const [achievementidentifier, setAchievementidentifier] = useState(null);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  useEffect(() => {
    const initializeTemplate = async () => {
      const storedTemplateData = localStorage.getItem("event_template_data");
      const storedAchievements = localStorage.getItem("achievementsEligibility");
      const achievement_details = localStorage.getItem("achievement_details");

      if (storedAchievements) {
        try {
          const parsedAchievements = JSON.parse(storedAchievements);
          if (parsedAchievements.achievement_id) {
            setSeriesId(parsedAchievements.achievement_id);
          }
        } catch (error) {
          console.error("Error parsing achievements:", error);
        }
      }

      if (achievement_details) {
        try {
          const parsedAchievements = JSON.parse(achievement_details);
          if (parsedAchievements.id) {
            setAchievementId(parsedAchievements.id);
          }
          if (parsedAchievements.identifier) {
            setAchievementidentifier(parsedAchievements.identifier);
          }
        } catch (error) {
          console.error("Error parsing achievements:", error);
        }
      }

      if (storedTemplateData) {
        try {
          const parsedData = JSON.parse(storedTemplateData);
          setTemplateData(parsedData);

          if (parsedData.template_image) {
            setTemplateImage(parsedData.template_image);
          }

          if (parsedData?.template_config?.output?.resolution?.length === 2) {
            setTemplateDimensions({
              width: parsedData.template_config.output.resolution[0],
              height: parsedData.template_config.output.resolution[1]
            });
          }

          if (parsedData?.template_config?.text) {
            const fontPromises = parsedData.template_config.text
              .filter(textConfig => textConfig?.font?.file)
              .map(textConfig => FontLoader.loadFont(textConfig.font.file));

            await Promise.all(fontPromises);
            setFontsLoaded(true); // Only set once
          } else {
            setFontsLoaded(true); // No fonts to load
          }
        } catch (error) {
          console.error("Error parsing template data:", error);
          setFontsLoaded(true); // Fallback
        }
      } else {
        setFontsLoaded(true);
      }
    };

    initializeTemplate();
  }, []);

  // Get runner name from localStorage
  useEffect(() => {
    try {
      const eventPhotosData = JSON.parse(localStorage.getItem('event_photos_data') || '[]');
      const participantName = eventPhotosData[0]?.participant_details?.name || '';
      if (participantName) {
        setRunnerName(participantName);
      }
    } catch (e) {
      console.warn("Failed to parse event_photos_data:", e);
    }
  }, []);

  // ============================================================================
  // ROBUST SCALE FACTOR CALCULATION (FIXED)
  // ============================================================================
  useEffect(() => {
    setScaleFactor(1); // Reset on mount

    let animationFrameId;
    let resizeTimeout;

    const calculateScale = () => {
      if (!previewContainerRef.current || !fontsLoaded) return;

      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        const containerWidth = previewContainerRef.current.offsetWidth;
        if (containerWidth > 0) {
          const scale = containerWidth / templateDimensions.width;
          setScaleFactor(scale);
        }
      });
    };

    const debouncedCalculate = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateScale, 100);
    };

    // Initial calculation after fonts load and layout settles
    if (fontsLoaded) {
      setTimeout(calculateScale, 50);
    }

    window.addEventListener('resize', debouncedCalculate);

    return () => {
      window.removeEventListener('resize', debouncedCalculate);
      cancelAnimationFrame(animationFrameId);
      clearTimeout(resizeTimeout);
    };
  }, [templateDimensions.width, fontsLoaded]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  const formatRunnerName = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getSafeImageUrl = (processedImage, field) => {
    if (!processedImage) return "";

    if (processedImage.processedImageUrl) {
      return processedImage.processedImageUrl;
    }

    const highResUrl = field?.selectedImage?.high_res_url;
    const s3Url = field?.selectedImage?.s3;

    return (
      (typeof highResUrl === 'string' ? highResUrl : highResUrl?.url || highResUrl?.s3) ||
      (typeof s3Url === 'string' ? s3Url : s3Url?.url || s3Url?.s3) ||
      ""
    );
  };

  const canvasToBlob = (canvas) => {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Canvas to Blob conversion failed"));
        }
      }, "image/jpeg", 0.95);
    });
  };


  // Add this helper function for sorted data
  const getSortedIndices = () => {
    const templateData = JSON.parse(localStorage.getItem('event_template_data') || "{}");
    const requiredSequence =
      templateData?.template?.config?.required_sequence ||
      templateData?.config?.required_sequence ||
      templateData?.required_sequence ||
      templateData?.template_config?.required_sequence ||
      [];

    if (requiredSequence.length === 0) {
      return eventFields.map((_, idx) => idx);
    }

    // Create sorted indices based on required_sequence
    return [...eventFields]
      .map((field, originalIndex) => ({ field, originalIndex }))
      .sort((a, b) => {
        const aIndex = requiredSequence.findIndex((seq) => seq.event_id === a.field.eventId);
        const bIndex = requiredSequence.findIndex((seq) => seq.event_id === b.field.eventId);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      })
      .map(item => item.originalIndex);
  };

  // ============================================================================
  // CERTIFICATE RENDERING (SHARED LOGIC)
  // ============================================================================

  const renderCertificateContent = (isDownload = false) => {
    const eventPhotosData = JSON.parse(localStorage.getItem('event_photos_data') || '[]');
    const imagePositions = templateData?.template_config?.images || [];
    const { width: templateW, height: templateH } = templateDimensions;

    return (
      <>
        {/* Template Background */}
        <img
          src={templateImage}
          alt="Certificate Template"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 20,
          }}
          crossOrigin="anonymous"
        />

        {/* Participant Name */}
        {runnerName && (() => {
          const nameConfig = templateData?.template_config?.text?.[0];
          if (!nameConfig) return null;

          let positionStyle = {};
          let fontSize = nameConfig.font?.size || 60;

          if (isDownload) {
            // DOWNLOAD: Use absolute pixel positions
            if (typeof nameConfig.position?.x === 'string' && nameConfig.position.x.includes('text_w')) {
              positionStyle = {
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${nameConfig.position.y}px`,
              };
            } else {
              positionStyle = {
                left: `${nameConfig.position?.x || 0}px`,
                top: `${nameConfig.position?.y || 0}px`,
              };
            }
          } else {
            // PREVIEW: Use percentage positions with proper scaling
            if (typeof nameConfig.position?.x === 'string' && nameConfig.position.x.includes('text_w')) {
              positionStyle = {
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${(nameConfig.position.y / templateH) * 100}%`,
              };
            } else {
              positionStyle = {
                left: `${(nameConfig.position?.x / templateW) * 100}%`,
                top: `${(nameConfig.position?.y / templateH) * 100}%`,
              };
            }
            fontSize = Math.round(fontSize * scaleFactor);
          }

          return (
            <DynamicFontText
              fontName={nameConfig.font?.file.split('/').pop().replace('.ttf', '') || 'CustomFont'}
              fontPath={nameConfig.font?.file}
              style={{
                position: "absolute",
                ...positionStyle,
                fontSize: isDownload ? `${nameConfig.font?.size}px` : `${fontSize}px`,
                color: nameConfig.font?.color || "#000",
                whiteSpace: 'nowrap',
                zIndex: 20,
                margin: 0,
                padding: 0,
                lineHeight: 1,
              }}
            >
              {formatRunnerName(runnerName)}
            </DynamicFontText>
          );
        })()}
        {/* Participant Photos */}
        {(() => {
          const eventPhotosData = JSON.parse(localStorage.getItem('event_photos_data') || '[]');
          const templateData = JSON.parse(localStorage.getItem('event_template_data') || "{}");
          const imagePositions = templateData?.template_config?.images || [];

          // Get sorted indices to match the order from EventImageProcessor
          const sortedIndices = getSortedIndices();

          // Map images to template positions using sorted order
          const orderedImages = imagePositions.map((positionConfig, idx) => {
            const targetEventId = positionConfig.event_id;

            if (idx < sortedIndices.length) {
              const originalIndex = sortedIndices[idx]; // Use sorted index
              return {
                processedImage: processedImages[originalIndex],
                field: eventFields[originalIndex],
                position: positionConfig,
                eventId: targetEventId,
                slot: positionConfig.slot,
                originalIndex: originalIndex
              };
            }
            return null;
          }).filter(Boolean);

          // Render images based on sorted order
          return orderedImages.map((item, index) => {
            if (!item.processedImage || !item.position) {
              console.warn(`Skipping slot ${item?.slot} - missing image or position`);
              return null;
            }

            const { processedImage, field, position, originalIndex } = item;
            let positionStyle = {};

            if (isDownload) {
              positionStyle = {
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${position.width}px`,
                height: `${position.height}px`,
              };
            } else {
              positionStyle = {
                left: `${(position.x / templateW) * 100}%`,
                top: `${(position.y / templateH) * 100}%`,
                width: `${(position.width / templateW) * 100}%`,
                height: `${(position.height / templateH) * 100}%`,
              };
            }

            return (
              <Box
                key={`photo-${position.event_id}-${position.slot}-${index}`}
                sx={{
                  position: "absolute",
                  ...positionStyle,
                  overflow: 'hidden',
                  zIndex: 5
                }}
              >
                <img
                  src={getSafeImageUrl(processedImage, field)}
                  alt={`${position.event_id} - Slot ${position.slot}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    display: "block",
                  }}
                  crossOrigin="anonymous"
                />
              </Box>
            );
          });
        })()}

        { }
        {(() => {
          const eventPhotosData = JSON.parse(localStorage.getItem("event_photos_data") || "[]");
          const templateData = JSON.parse(localStorage.getItem("event_template_data") || "{}");
          const finishTimeConfigs = templateData?.template_config?.text?.slice(1) || [];

          const requiredSequence =
            templateData?.config?.required_sequence ||
            templateData?.required_sequence ||
            templateData?.template_config?.required_sequence ||
            [];

          // Get sorted indices
          const sortedIndices = getSortedIndices();

          // Create ordered finish times using sorted indices
          let orderedFinishTimes = [];

          if (requiredSequence.length > 0) {
            orderedFinishTimes = sortedIndices.map((originalIdx) => {
              const eventId = eventFields[originalIdx].eventId;
              const matchingPhoto = eventPhotosData.find(p => p.event_id === eventId);

              if (matchingPhoto) {
                return matchingPhoto.participant_details?.finish_time || "N/A";
              } else {
                console.warn(`No match found for event_id: ${eventId}`);
                return "N/A";
              }
            });
          } else {
            console.warn("required_sequence is empty, using original order");
            orderedFinishTimes = eventPhotosData.map(p =>
              p.participant_details?.finish_time || "N/A"
            );
          }

          // Render finish times
          return finishTimeConfigs.map((textConfig, index) => {
            const finishTime = orderedFinishTimes[index] || "N/A";

            let positionStyle = {};
            let fontSize = textConfig.font?.size || 70;

            if (isDownload) {
              positionStyle = {
                left: `${textConfig.position?.x || 0}px`,
                top: `${textConfig.position?.y || 0}px`,
              };
            } else {
              positionStyle = {
                left: `${(textConfig.position?.x * scaleFactor)}px`,
                top: `${(textConfig.position?.y * scaleFactor)}px`,
              };
              fontSize = Math.round(fontSize * scaleFactor);
            }

            const fontName = textConfig.font?.file
              ?.split('/')
              .pop()
              .replace('.ttf', '') || 'CustomFont';
            const fontPath = textConfig.font?.file;

            return (
              <DynamicFontText
                key={`time-${index}`}
                fontName={fontName}
                fontPath={fontPath}
                style={{
                  position: "absolute",
                  ...positionStyle,
                  fontSize: isDownload ? `${textConfig.font?.size}px` : `${fontSize}px`,
                  color: textConfig.font?.color || "#000",
                  whiteSpace: 'nowrap',
                  zIndex: 20,
                  margin: 0,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                {finishTime}
              </DynamicFontText>
            );
          });
        })()}
      </>
    );
  };

  // ============================================================================
  // DOWNLOAD HANDLER
  // ============================================================================

  // Final Download Handler (Local + API)
  const handleDownload = async () => {
    const eventPhotosData = JSON.parse(localStorage.getItem('event_photos_data') || '[]');
    const participantName =
      runnerName || eventPhotosData[0]?.participant_details?.name || 'Participant';

    if (!participantName.trim()) {
      snack.enqueueSnackbar("Participant name not found.", { variant: "warning" });
      return;
    }

    if (!achievementId) {
      snack.enqueueSnackbar({
        variant: "error",
        message: "Achievement ID not found. Cannot proceed with API upload.",
      });
      return;
    }

    setLoading(true);

    try {
      // Generate high-resolution certificate (same as local logic)
      const canvas = await html2canvas(downloadContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        scale: 1,
        width: templateDimensions.width,
        height: templateDimensions.height,
        logging: false,
      });

      // Convert to Blob (same as local logic)
      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.95)
      );

      if (!blob) {
        snack.enqueueSnackbar("Failed to generate certificate image.", { variant: "error" });
        return;
      }

      const certificateFilename =
        templateData?.template_config?.output?.filename || "Certificate.jpeg";

      // Upload to API
      const formData = new FormData();
      formData.append("achievement_id", achievementId);
      formData.append("seriesId", seriesId);
      formData.append("achievementidentifier", achievementidentifier);
      formData.append(
        "certificate_url",
        blob,
        `${participantName.replace(/[^a-zA-Z0-9]/g, '')}_${certificateFilename}`
      );

      const response = await axios.post(ApiManager.certificatedownload(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      snack.enqueueSnackbar({
        variant: "success",
        message: response.data.message || "Certificate uploaded successfully!",
      });

      // If API returns hosted certificate URL → Download from server too
      if (response.data?.success && response.data?.certificate_url) {
        const certificateUrl = response.data.certificate_url;

        try {
          const downloadResponse = await fetch(certificateUrl, {
            mode: "cors",
            credentials: "omit",
          });

          if (!downloadResponse.ok) {
            throw new Error(`Failed to fetch certificate: ${downloadResponse.status}`);
          }

          const downloadBlob = await downloadResponse.blob();
          const downloadUrl = window.URL.createObjectURL(downloadBlob);
          const apiLink = document.createElement("a");
          apiLink.href = downloadUrl;
          apiLink.download = `${participantName.replace(/[^a-zA-Z0-9]/g, '')}_${certificateFilename}`;
          apiLink.style.display = "none";

          document.body.appendChild(apiLink);
          apiLink.click();

          setTimeout(() => {
            document.body.removeChild(apiLink);
            window.URL.revokeObjectURL(downloadUrl);
          }, 100);

          snack.enqueueSnackbar("Certificate downloaded Successfully", { variant: "success" });
        } catch (downloadError) {
          console.error("Error downloading certificate:", downloadError);
          window.open(certificateUrl, "_blank");
          snack.enqueueSnackbar(
            "Certificate opened in new tab. Please save it manually.",
            { variant: "info" }
          );
        }
      }
    } catch (error) {
      console.error("Error generating or uploading certificate:", error);

      const message =
        error.name === "SecurityError"
          ? "Unable to generate certificate due to image security restrictions (CORS)."
          : `Failed to upload certificate. Error: ${error.message || "Unknown error"}`;

      snack.enqueueSnackbar({ variant: "error", message });
    } finally {
      setLoading(false);
    }
  };



  // ============================================================================
  // RENDER
  // ============================================================================
  if (!templateImage || !fontsLoaded) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#3F0E77' }} />
        <Typography sx={{ mt: 2 }}>Loading template and fonts...</Typography>
      </Container>
    );
  }

  const aspectRatio = templateDimensions.width / templateDimensions.height;

  return (
    <Container maxWidth="xl" sx={{ py: 3, pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 18, md: 20 },
            fontWeight: 600,
            color: "#3F0E77",
          }}
        >
          Preview and Download Your Certificate
        </Typography>
        <Box
          sx={{
            flexGrow: 1,
            height: "1px",
            backgroundColor: ColorHelper.LightPurple,
            mx: 2,
          }}
        ></Box>

      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Certificate Preview
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleDownload}
                  disabled={loading}
                  startIcon={<DownloadIcon />}
                  sx={{
                    bgcolor: "#3F0E77",
                    "&:hover": { bgcolor: "#2F0A5A" },
                    textTransform: "none",
                    fontWeight: "bold",
                    px: 3,
                  }}
                >
                  {loading ? "Downloading..." : "Download"}
                </Button>
              </Box>

              {/* PREVIEW CONTAINER (Responsive) */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingBottom: `${(1 / aspectRatio) * 100}%`,
                  overflow: "hidden",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                }}
              >
                <Box
                  ref={previewContainerRef}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {renderCertificateContent(false)}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DOWNLOAD CONTAINER (Hidden, Full Resolution) */}
      <Box
        ref={downloadContainerRef}
        sx={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: `${templateDimensions.width}px`,
          height: `${templateDimensions.height}px`,
          overflow: "hidden",
        }}
      >
        {renderCertificateContent(true)}
      </Box>

      {/* Bottom Navigation */}
      <AppBar
        position="fixed"
        color="default"
        sx={{
          top: "auto",
          bottom: 0,
          bgcolor: "background.paper",
          boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
          height: 90,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: 2, height: "100%", alignItems: "center" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: "#3F0E77",
              borderColor: "#3F0E77",
              fontWeight: '500',
              "&:hover": {
                backgroundColor: "#3F0E77",
                color: "white",
                borderColor: "#3F0E77",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={onNavigateToImageProcessor}
            startIcon={<BackIcon />}
            sx={{
              color: "#3F0E77",
              borderColor: "#3F0E77",
              fontWeight: '500',
              "&:hover": {
                backgroundColor: "#3F0E77",
                color: "white",
                borderColor: "#3F0E77",
              },
            }}
          >
            Back to Edit
          </Button>
        </Toolbar>
      </AppBar>
    </Container>
  );
};

export default EventCertificateGenerator;